import React, { useEffect, useState } from 'react'
import Container from '../components/Container';
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux';
import DataFetching from "../utils/DataFetching";
import renderDataTable from "../utils/renderDataTable";
import Breadcrumb from '../components/Breadcrumb';

/* ---------------- API helper (edit + delete mate) ---------------- */

// Same base URL jem tamara curl ma che: .../admin/edit_lead_management.php
// (List API mate tamaro j DataFetching vapryo che, tema '/get_lead_management.php' aapyu che.)
const API_BASE = "https://tezbikes.com/tezbike/api/v1/I8JLS9KH9/admin";

async function callAdminApi(endpoint, token, body) {
    const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token },
        body: JSON.stringify(body),
    });

    let data = null;
    try {
        data = await res.json();
    } catch {
        data = null;
    }

    if (!res.ok || data?.success === false || data?.status === false) {
        throw new Error(data?.message || "Request failed. Please try again.");
    }
    return data;
}

/* ---------------- Helpers ---------------- */

// API response ma date nu field nu naam khabar nathi, etle 3 common naam check kariye chhiye.
// Network tab ma response joi ne barabar naam rakhjo.
const getEnquiryDate = (row) => row.enquiry_date || row.created_at || row.date || "";

function formatDate(value) {
    if (!value) return "—";
    // "2026-09-21" ke "2026-09-21 11:24:00" mathi fakt date leva (timezone ni problem na aave)
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = match
        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

const inputClass =
    "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100";

function Modal({ title, onClose, children }) {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <h2 className="text-lg font-extrabold text-neutral-900">{title}</h2>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                    >
                        ✕
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

/* ---------------- Page ---------------- */

const Leadmanagement = () => {
    const location = useLocation();
    const token = useSelector((state) => state.user.userToken)

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [filteredData, setFilteredData] = useState([]);
    const [pending, setPending] = useState(true);
    const [resetSearch, setResetSearch] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // View / Edit / Delete popup state
    const [modal, setModal] = useState(null); // 'view' | 'edit' | 'delete' | null
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ name: "", phone_number: "", bike_requirement: "" });
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");

    /* ----- GET: lead list ----- */
    const fetchData = async () => {
        setPending(true);

        const body = {
            page: currentPage - 1,
            perpage: pageLimit,
        }

        DataFetching(setPending, '/get_lead_management.php', 'POST', token, dispatch, navigate, setFilteredData, setTotalPages, body, setResetSearch, resetSearch);
    };

    // Render the DataTable component
    useEffect(() => {
        if (!resetSearch) {
            return;
        }
        fetchData();
    }, [currentPage, pageLimit, location, resetSearch]);

    /* ----- Popup open / close ----- */
    const closeModal = () => {
        setModal(null);
        setSelected(null);
        setModalError("");
        setSaving(false);
    };

    const openView = (row) => {
        setSelected(row);
        setModal("view");
    };

    const openEdit = (row) => {
        setSelected(row);
        setForm({
            name: row.name ?? "",
            phone_number: row.phone_number ?? "",
            bike_requirement: row.bike_requirement ?? "",
        });
        setModalError("");
        setModal("edit");
    };

    const openDelete = (row) => {
        setSelected(row);
        setModalError("");
        setModal("delete");
    };

    /* ----- EDIT API ----- */
    const handleSave = async (e) => {
        e.preventDefault();

        const digits = form.phone_number.replace(/\D/g, "");
        if (!form.name.trim() || !form.phone_number.trim() || !form.bike_requirement.trim()) {
            setModalError("Name, phone number and bike requirement are required.");
            return;
        }
        if (digits.length < 10) {
            setModalError("Enter a valid phone number (at least 10 digits).");
            return;
        }

        setSaving(true);
        setModalError("");
        try {
            await callAdminApi("/edit_lead_management.php", token, {
                id: String(selected.id),
                name: form.name.trim(),
                phone_number: form.phone_number.trim(),
                bike_requirement: form.bike_requirement.trim(),
            });
            closeModal();
            fetchData(); // list refresh
        } catch (err) {
            setModalError(err.message);
            setSaving(false);
        }
    };

    /* ----- DELETE API ----- */
    const handleDelete = async () => {
        setSaving(true);
        setModalError("");
        try {
            await callAdminApi("/delete_lead_management.php", token, {
                id: String(selected.id),
            });
            const wasLastRowOnPage = filteredData.length === 1 && currentPage > 1;
            closeModal();
            if (wasLastRowOnPage) setCurrentPage(currentPage - 1); // useEffect fetch karse
            else fetchData();
        } catch (err) {
            setModalError(err.message);
            setSaving(false);
        }
    };

    /* ----- Table columns ----- */
    const column = [
        {
            name: 'ID',
            selector: row => row.id,
            width: '7%'
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: "Phone",
            selector: (row) => row.phone_number,
            sortable: true,
        },
        {
            name: 'Bike Requirement',
            selector: row => row.bike_requirement,
            sortable: true,
            wrap: true,
            grow: 2,
        },
        {
            name: 'Enquiry Date',
            selector: row => getEnquiryDate(row),
            cell: row => formatDate(getEnquiryDate(row)),
            sortable: true,
            minWidth: '170px',
        },
        {
            name: 'Actions',
            minWidth: '220px',
            ignoreRowClick: true,
            cell: row => (
                <div className="flex items-center gap-2">

                    <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => openDelete(row)}
                        className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                    >
                        Delete
                    </button>
                </div>
            ),
        },
    ]


    return (
        <>
            <Breadcrumb pageName="Lead Management" />
            <Container>
                <>
                    <div className="border-2 border-solid border-[#ddddddbe] w-full">
                        <div className='p-3 w-full'>
                            <div className='py-3'>
                                {renderDataTable(
                                    filteredData,
                                    column,
                                    pending,
                                    currentPage,
                                    setPageLimit,
                                    setCurrentPage,
                                    totalPages,
                                    pageLimit,
                                    setResetSearch,
                                    dispatch,
                                    navigate,
                                )}
                            </div>
                        </div>
                    </div>
                </>
            </Container>

            {/* View */}
            {modal === "view" && selected && (
                <Modal title="Enquiry details" onClose={closeModal}>
                    <dl className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 text-sm">
                        {[
                            ["Name", selected.name],
                            ["Phone number", selected.phone_number],
                            ["Bike requirement", selected.bike_requirement],
                            ["Enquiry date", formatDate(getEnquiryDate(selected))],
                        ].map(([label, value]) => (
                            <div key={label} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:justify-between sm:gap-6">
                                <dt className="shrink-0 text-neutral-500">{label}</dt>
                                <dd className="break-words font-semibold text-neutral-900 sm:text-right">{value || "—"}</dd>
                            </div>
                        ))}
                    </dl>
                    <div className="mt-5 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => openEdit(selected)}
                            className="rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white hover:bg-orange-600"
                        >
                            Edit lead
                        </button>
                    </div>
                </Modal>
            )}

            {/* Edit */}
            {modal === "edit" && selected && (
                <Modal title="Edit lead" onClose={closeModal}>
                    <form onSubmit={handleSave} noValidate className="space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-neutral-900">Name</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-neutral-900">Phone number</label>
                            <input
                                type="tel"
                                inputMode="tel"
                                maxLength={16}
                                value={form.phone_number}
                                onChange={(e) =>
                                    setForm({ ...form, phone_number: e.target.value.replace(/[^\d+\s-]/g, "") })
                                }
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-neutral-900">Bike requirement</label>
                            <textarea
                                rows={4}
                                value={form.bike_requirement}
                                onChange={(e) => setForm({ ...form, bike_requirement: e.target.value })}
                                className={`${inputClass} resize-none`}
                            />
                        </div>

                        {modalError && (
                            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                                {modalError}
                            </p>
                        )}

                        <div className="flex justify-end gap-3 pt-1">
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-full border border-neutral-300 px-5 py-2 text-sm font-bold text-neutral-800 hover:bg-neutral-100"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="rounded-full bg-blue-500 px-5 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-60"
                            >
                                {saving ? "Saving..." : "Save changes"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Delete */}
            {modal === "delete" && selected && (
                <Modal title="Delete this lead?" onClose={closeModal}>
                    <p className="text-sm leading-relaxed text-neutral-600">
                        <span className="font-semibold text-neutral-900">{selected.name}</span>{" "}
                        ({selected.phone_number}) will be permanently removed. This cannot be undone.
                    </p>

                    {modalError && (
                        <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                            {modalError}
                        </p>
                    )}

                    <div className="mt-5 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="rounded-full border border-neutral-300 px-5 py-2 text-sm font-bold text-neutral-800 hover:bg-neutral-100"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={saving}
                            className="rounded-full bg-red-600 px-5 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                            {saving ? "Deleting..." : "Yes, delete"}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    )
}

export default Leadmanagement;